import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import {
    Loader2,
    CalendarRange,
    CheckCircle2,
    UserX,
    Clock,
    TrendingUp,
    DollarSign,
    Stethoscope,
    Users,
    ChevronLeft,
    FileDown,
    Lightbulb,
    Flame,
    Snowflake,
    Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import "react-day-picker/dist/style.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPANISH_MONTHS = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function toISODate(d) {
    if (!d) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function formatRange(from, to) {
    if (!from || !to) return "—";
    const f = new Date(`${from}T00:00:00`);
    const t = new Date(`${to}T00:00:00`);
    const sameYear = f.getFullYear() === t.getFullYear();
    const sameMonth = sameYear && f.getMonth() === t.getMonth();
    if (sameMonth) {
        return `${f.getDate()}–${t.getDate()} de ${SPANISH_MONTHS[t.getMonth()]} ${t.getFullYear()}`;
    }
    if (sameYear) {
        return `${f.getDate()} ${SPANISH_MONTHS[f.getMonth()]} – ${t.getDate()} ${SPANISH_MONTHS[t.getMonth()]} ${t.getFullYear()}`;
    }
    return `${f.getDate()} ${SPANISH_MONTHS[f.getMonth()]} ${f.getFullYear()} – ${t.getDate()} ${SPANISH_MONTHS[t.getMonth()]} ${t.getFullYear()}`;
}

function getRangeForPreset(preset) {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();
    if (preset === "all") {
        // "Toda la historia" — sentinel range; backend will use the actual DB
        // extremes when from/to are omitted. We return nulls to skip params.
        return { from: null, to: null };
    }
    if (preset === "today") {
        return { from: new Date(y, m, d), to: new Date(y, m, d) };
    }
    if (preset === "week") {
        const dow = (today.getDay() + 6) % 7; // Mon=0..Sun=6
        const from = new Date(y, m, d - dow);
        const to = new Date(y, m, d - dow + 6);
        return { from, to };
    }
    if (preset === "month") {
        return { from: new Date(y, m, 1), to: new Date(y, m + 1, 0) };
    }
    if (preset === "year") {
        return { from: new Date(y, 0, 1), to: new Date(y, 11, 31) };
    }
    return null;
}

const PRESETS = [
    { id: "all", label: "Todo" },
    { id: "today", label: "Hoy" },
    { id: "week", label: "Esta semana" },
    { id: "month", label: "Este mes" },
    { id: "year", label: "Este año" },
];

function StatCard({ icon: Icon, label, value, sub, accent = "fg" }) {
    const accentColor = {
        fg: "text-foreground",
        primary: "text-primary",
        destructive: "text-destructive",
        muted: "text-muted-foreground",
    }[accent] || "text-foreground";
    return (
        <Card
            className="rounded-2xl border-border bg-card p-5 soft-shadow"
            data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="flex items-center gap-2 mb-2">
                {Icon && <Icon className={`h-4 w-4 ${accentColor}`} />}
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                    {label}
                </p>
            </div>
            <p className={`font-heading text-3xl font-medium tracking-tight leading-none ${accentColor}`}>
                {value}
            </p>
            {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
        </Card>
    );
}

function BarRow({ label, count, total, color = "bg-primary" }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="flex items-center gap-3">
            <p className="w-28 sm:w-32 shrink-0 text-sm font-medium truncate" title={label}>
                {label}
            </p>
            <div className="flex-1 h-7 bg-muted rounded-lg overflow-hidden relative">
                <div
                    className={`h-full ${color} transition-all duration-500 rounded-lg`}
                    style={{ width: `${Math.max(pct, count > 0 ? 4 : 0)}%` }}
                />
                <div className="absolute inset-0 flex items-center px-2.5">
                    <span className="text-xs font-semibold text-foreground/85 tabular-nums">
                        {count}
                    </span>
                    {pct > 0 && (
                        <span className="text-xs text-muted-foreground ml-1.5">· {pct}%</span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AdminStatistics({ onBack }) {
    const [preset, setPreset] = useState("all");
    const [customRange, setCustomRange] = useState(() => getRangeForPreset("month"));
    const [showCustom, setShowCustom] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);

    const range = useMemo(() => {
        if (preset === "custom") return customRange;
        return getRangeForPreset(preset);
    }, [preset, customRange]);

    useEffect(() => {
        // For preset "all" both range.from and range.to are null — we send no
        // params so the backend defaults to the full DB range.
        const isAll = preset === "all";
        if (!isAll && (!range?.from || !range?.to)) return;
        const ctrl = new AbortController();
        const run = async () => {
            try {
                const params = isAll
                    ? {}
                    : { from: toISODate(range.from), to: toISODate(range.to) };
                const res = await axios.get(`${API}/admin/statistics`, {
                    params,
                    withCredentials: true,
                    signal: ctrl.signal,
                });
                setData(res.data);
                setError(null);
            } catch (err) {
                if (err?.name === "CanceledError") return;
                setError(err?.response?.data?.detail || "No se pudo cargar las estadísticas");
                toast.error("Error al cargar estadísticas");
            } finally {
                setLoading(false);
            }
        };
        run();
        return () => ctrl.abort();
    }, [range, preset]);

    // For "all" we trust the dates the backend returned; for everything else,
    // we use the range we computed locally.
    const fromISO = preset === "all" ? data?.from || null : (range?.from ? toISODate(range.from) : null);
    const toISO = preset === "all" ? data?.to || null : (range?.to ? toISODate(range.to) : null);
    const maxWeekday = useMemo(
        () => (data?.by_weekday ? Math.max(1, ...data.by_weekday.map((d) => d.count)) : 1),
        [data]
    );
    const maxHour = useMemo(
        () => (data?.by_hour ? Math.max(1, ...data.by_hour.map((d) => d.count)) : 1),
        [data]
    );

    const handleExportPdf = async () => {
        if (!fromISO || !toISO) return;
        setExporting(true);
        try {
            const res = await axios.get(`${API}/admin/statistics/export.pdf`, {
                params: { from: fromISO, to: toISO },
                withCredentials: true,
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = `reporte_estadisticas_${fromISO}_${toISO}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("PDF generado");
        } catch (err) {
            console.error("export PDF failed:", err);
            toast.error("No se pudo generar el PDF");
        } finally {
            setExporting(false);
        }
    };


    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border bg-card">
                <div className="max-w-6xl mx-auto px-5 py-5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onBack}
                            className="rounded-xl"
                            data-testid="stats-back-btn"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Citas
                        </Button>
                        <div className="min-w-0">
                            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                                Panel de control
                            </p>
                            <h1 className="font-heading text-xl sm:text-2xl font-medium tracking-tight truncate">
                                Estadísticas
                            </h1>
                        </div>
                    </div>
                    <Button
                        onClick={handleExportPdf}
                        disabled={exporting || loading || !data}
                        className="rounded-2xl shrink-0"
                        data-testid="stats-export-pdf-btn"
                    >
                        {exporting ? (
                            <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                        ) : (
                            <FileDown className="h-4 w-4 sm:mr-2" />
                        )}
                        <span className="hidden sm:inline">Exportar PDF</span>
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 py-6 sm:py-10">
                {/* Range picker */}
                <Card className="rounded-2xl border-border bg-card p-4 sm:p-5 mb-6 soft-shadow">
                    <div className="flex flex-wrap items-center gap-2">
                        <CalendarRange className="h-4 w-4 text-muted-foreground shrink-0" />
                        <p className="text-sm font-medium mr-2">Periodo:</p>
                        {PRESETS.map((p) => (
                            <Button
                                key={p.id}
                                size="sm"
                                variant={preset === p.id ? "default" : "outline"}
                                onClick={() => {
                                    setPreset(p.id);
                                    setShowCustom(false);
                                }}
                                className="rounded-full h-8 text-xs"
                                data-testid={`stats-preset-${p.id}`}
                            >
                                {p.label}
                            </Button>
                        ))}
                        <Button
                            size="sm"
                            variant={preset === "custom" ? "default" : "outline"}
                            onClick={() => {
                                setPreset("custom");
                                setShowCustom((v) => !v);
                            }}
                            className="rounded-full h-8 text-xs"
                            data-testid="stats-preset-custom"
                        >
                            Personalizado
                        </Button>
                        <div className="ml-auto text-xs text-muted-foreground" data-testid="stats-range-label">
                            {formatRange(fromISO, toISO)}
                        </div>
                    </div>

                    {showCustom && (
                        <div className="mt-4 pt-4 border-t border-border">
                            <Calendar
                                mode="range"
                                selected={{ from: customRange.from, to: customRange.to }}
                                onSelect={(r) => {
                                    if (r?.from && r?.to) {
                                        setCustomRange(r);
                                    }
                                }}
                                numberOfMonths={1}
                                className="rounded-lg"
                            />
                        </div>
                    )}
                </Card>

                {loading && (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="h-7 w-7 animate-spin text-primary" />
                    </div>
                )}

                {error && !loading && (
                    <Card className="rounded-2xl border-destructive/30 bg-destructive/5 p-5 text-destructive text-sm">
                        {error}
                    </Card>
                )}

                {!loading && !error && data && (
                    <>
                        {/* KPI grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
                            <StatCard
                                icon={Users}
                                label="Total agendadas"
                                value={data.total}
                                accent="primary"
                            />
                            <StatCard
                                icon={CheckCircle2}
                                label="Atendidas"
                                value={data.attended}
                                sub={`${data.attendance_rate}% asistencia`}
                                accent="primary"
                            />
                            <StatCard
                                icon={UserX}
                                label="No-shows"
                                value={data.no_show}
                                sub={`${data.no_show_rate}% del total`}
                                accent="destructive"
                            />
                            <StatCard
                                icon={Clock}
                                label="Pendientes"
                                value={data.pending}
                                sub="hoy en adelante"
                                accent="muted"
                            />
                            <StatCard
                                icon={TrendingUp}
                                label="Tasa asistencia"
                                value={`${data.attendance_rate}%`}
                                sub="atendidas / decididas"
                                accent="primary"
                            />
                            <StatCard
                                icon={DollarSign}
                                label="Ingresos"
                                value={`$${data.revenue.toLocaleString("es-MX")}`}
                                sub="atenciones cobradas"
                                accent="primary"
                            />
                        </div>

                        {/* Ad attribution */}
                        {data.by_source && (
                            <Card
                                className="rounded-2xl border-border bg-card p-5 sm:p-6 soft-shadow mb-8"
                                data-testid="stats-source-card"
                            >
                                <div className="flex items-center gap-2 mb-4">
                                    <Megaphone className="h-4 w-4 text-primary" />
                                    <h2 className="font-heading text-lg font-medium">
                                        Origen de las citas
                                    </h2>
                                </div>
                                <div className="grid sm:grid-cols-3 gap-4 mb-4">
                                    <div className="rounded-xl bg-[#1877F2]/10 p-4" data-testid="stats-meta-ads-count">
                                        <p className="text-xs text-muted-foreground mb-1">📣 Meta Ads</p>
                                        <p className="font-heading text-2xl font-semibold text-[#1877F2]">
                                            {data.by_source.meta_ads}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {data.total
                                                ? Math.round((data.by_source.meta_ads / data.total) * 100)
                                                : 0}
                                            % del total
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-muted p-4" data-testid="stats-organic-count">
                                        <p className="text-xs text-muted-foreground mb-1">Orgánico / directo</p>
                                        <p className="font-heading text-2xl font-semibold">
                                            {data.by_source.organic}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {data.total
                                                ? Math.round((data.by_source.organic / data.total) * 100)
                                                : 0}
                                            % del total
                                        </p>
                                    </div>
                                    <div className="rounded-xl bg-primary/10 p-4" data-testid="stats-ads-revenue">
                                        <p className="text-xs text-muted-foreground mb-1">Ingresos vía Ads</p>
                                        <p className="font-heading text-2xl font-semibold text-primary">
                                            ${(data.by_source.ads_revenue || 0).toLocaleString("es-MX")}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {data.by_source.ads_attended} atendidas
                                        </p>
                                    </div>
                                </div>
                                {data.by_source.campaigns && data.by_source.campaigns.length > 0 && (
                                    <div className="space-y-1.5" data-testid="stats-campaigns-list">
                                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                            Por campaña
                                        </p>
                                        {data.by_source.campaigns.map((c) => (
                                            <div
                                                key={c.campaign}
                                                className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg bg-muted/50"
                                            >
                                                <span className="truncate">{c.campaign}</span>
                                                <span className="font-semibold tabular-nums ml-3">{c.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {data.by_source.meta_ads === 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        Aún no hay citas provenientes de anuncios. Asegúrate de que el enlace de tus
                                        anuncios incluya <code className="font-mono">utm_source=facebook</code> (o que
                                        Meta agregue el <code className="font-mono">fbclid</code> automático).
                                    </p>
                                )}
                            </Card>
                        )}

                        {/* Nurses + Procedures */}
                        <div className="grid lg:grid-cols-2 gap-6 mb-8">
                            <Card className="rounded-2xl border-border bg-card p-5 sm:p-6 soft-shadow">
                                <div className="flex items-center gap-2 mb-4">
                                    <Users className="h-4 w-4 text-primary" />
                                    <h2 className="font-heading text-lg font-medium">
                                        Citas por enfermera
                                    </h2>
                                </div>
                                {data.by_nurse.length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-6 text-center">
                                        Sin atenciones registradas en este periodo
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {data.by_nurse.map((n) => {
                                            const pct = data.attended
                                                ? Math.round((n.count / data.attended) * 100)
                                                : 0;
                                            const barPct = Math.max(pct, n.count > 0 ? 4 : 0);
                                            return (
                                                <div
                                                    key={n.nurse}
                                                    data-testid={`stats-nurse-${n.nurse}`}
                                                >
                                                    <div className="flex items-center justify-between text-sm mb-1">
                                                        <span className="font-medium">{n.nurse}</span>
                                                        <span className="font-semibold text-primary tabular-nums">
                                                            ${(n.revenue || 0).toLocaleString("es-MX")}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-1 h-7 bg-muted rounded-lg overflow-hidden relative">
                                                            <div
                                                                className="h-full bg-primary transition-all duration-500 rounded-lg"
                                                                style={{ width: `${barPct}%` }}
                                                            />
                                                            <div className="absolute inset-0 flex items-center px-2.5">
                                                                <span className="text-xs font-semibold text-foreground/85 tabular-nums">
                                                                    {n.count}
                                                                </span>
                                                                {pct > 0 && (
                                                                    <span className="text-xs text-muted-foreground ml-1.5">
                                                                        · {pct}%
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Card>

                            <Card className="rounded-2xl border-border bg-card p-5 sm:p-6 soft-shadow">
                                <div className="flex items-center gap-2 mb-4">
                                    <Stethoscope className="h-4 w-4 text-primary" />
                                    <h2 className="font-heading text-lg font-medium">
                                        Top procedimientos
                                    </h2>
                                </div>
                                {data.by_procedure.length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-6 text-center">
                                        Sin citas en este periodo
                                    </p>
                                ) : (
                                    <div className="space-y-2.5">
                                        {data.by_procedure.slice(0, 5).map((p) => (
                                            <BarRow
                                                key={p.procedure_id}
                                                label={p.name.replace(/\s*\(.*?\)\s*/g, "")}
                                                count={p.count}
                                                total={data.total || 1}
                                            />
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </div>

                        {/* Weekday distribution */}
                        <Card className="rounded-2xl border-border bg-card p-5 sm:p-6 soft-shadow">
                            <div className="flex items-center gap-2 mb-4">
                                <CalendarRange className="h-4 w-4 text-primary" />
                                <h2 className="font-heading text-lg font-medium">
                                    Distribución por día
                                </h2>
                            </div>
                            <div className="flex items-end justify-between gap-2 h-40 sm:h-48 mt-6">
                                {data.by_weekday.map((d) => {
                                    const pct = d.count > 0
                                        ? Math.max(8, (d.count / maxWeekday) * 100)
                                        : 0;
                                    return (
                                        <div
                                            key={d.day}
                                            className="flex-1 flex flex-col items-center gap-1.5"
                                            data-testid={`stats-weekday-${d.day}`}
                                        >
                                            <span className="text-xs font-semibold tabular-nums text-foreground/85">
                                                {d.count}
                                            </span>
                                            <div className="w-full bg-muted rounded-t-lg flex-1 flex items-end overflow-hidden">
                                                <div
                                                    className="w-full bg-primary rounded-t-lg transition-all duration-500"
                                                    style={{ height: `${pct}%` }}
                                                />
                                            </div>
                                            <span className="text-[11px] text-muted-foreground font-medium">
                                                {d.day}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>

                        {/* Hour distribution */}
                        <Card className="rounded-2xl border-border bg-card p-5 sm:p-6 soft-shadow mt-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Clock className="h-4 w-4 text-primary" />
                                <h2 className="font-heading text-lg font-medium">
                                    Distribución por horario
                                </h2>
                            </div>
                            {data.by_hour && data.by_hour.length > 0 ? (
                                <div className="space-y-2" data-testid="stats-hours">
                                    {data.by_hour.map((h) => (
                                        <BarRow
                                            key={h.time}
                                            label={h.time}
                                            count={h.count}
                                            total={maxHour}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground py-6 text-center">
                                    Sin citas en este periodo
                                </p>
                            )}
                        </Card>

                        {/* Insights / Recomendaciones */}
                        {data.insights && data.total > 0 && (
                            <Card
                                className="rounded-2xl border-primary/20 bg-primary/5 p-5 sm:p-6 soft-shadow mt-6"
                                data-testid="stats-insights"
                            >
                                <div className="flex items-center gap-2 mb-4">
                                    <Lightbulb className="h-4 w-4 text-primary" />
                                    <h2 className="font-heading text-lg font-medium">
                                        Oportunidades de promoción
                                    </h2>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <div className="flex items-start gap-3">
                                            <Flame className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                                    Día más concurrido
                                                </p>
                                                <p className="font-medium">
                                                    {data.insights.busiest_day.day}
                                                    <span className="text-muted-foreground font-normal text-sm ml-1.5">
                                                        ({data.insights.busiest_day.count} citas)
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <Flame className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                                    Horario pico
                                                </p>
                                                <p className="font-medium">
                                                    {data.insights.busiest_hour.time}
                                                    <span className="text-muted-foreground font-normal text-sm ml-1.5">
                                                        ({data.insights.busiest_hour.count} citas)
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-start gap-3">
                                            <Snowflake className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                                    Día con menos demanda
                                                </p>
                                                <p className="font-medium">
                                                    {data.insights.slowest_day.day}
                                                    <span className="text-muted-foreground font-normal text-sm ml-1.5">
                                                        ({data.insights.slowest_day.count} citas)
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <Snowflake className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                                    Horario más libre
                                                </p>
                                                <p className="font-medium">
                                                    {data.insights.slowest_hour.time}
                                                    <span className="text-muted-foreground font-normal text-sm ml-1.5">
                                                        ({data.insights.slowest_hour.count} citas)
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 pt-4 border-t border-primary/20">
                                    <p className="text-sm text-foreground/85 leading-relaxed">
                                        💡 Considera ofrecer una promoción los{" "}
                                        <strong className="text-primary">
                                            {data.insights.slowest_day.day.toLowerCase()}
                                        </strong>{" "}
                                        o en el horario de las{" "}
                                        <strong className="text-primary">
                                            {data.insights.slowest_hour.time}
                                        </strong>{" "}
                                        para incentivar reservas en los espacios menos
                                        solicitados.
                                    </p>
                                </div>
                            </Card>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
